import React from "react";
import { Link } from "react-router-dom";
import { anchorId } from "@/lib/slug";
import { refUrl } from "@/lib/api";

// Build a linker that turns known entity names inside plain text into wiki links.
// Only links the FIRST occurrence of each name across the whole body (like Wikipedia).
function buildLinker(refs) {
  const map = new Map(); // lower name -> ref
  (refs || []).forEach((r) => {
    if (r && r.name && r.name.length >= 3) map.set(r.name.toLowerCase(), r);
  });
  const names = Array.from(new Set(Array.from(map.keys())))
    .sort((a, b) => b.length - a.length);
  const used = new Set();

  if (names.length === 0) {
    return (text) => text;
  }
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`\\b(${escaped.join("|")})\\b`, "gi");

  return function linkify(text, keyBase) {
    const out = [];
    let last = 0;
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(text)) !== null) {
      const matched = m[0];
      const key = matched.toLowerCase();
      const ref = map.get(key);
      if (!ref || used.has(key)) continue;
      used.add(key);
      if (m.index > last) out.push(text.slice(last, m.index));
      out.push(
        <Link
          key={`${keyBase}-${m.index}`}
          to={refUrl(ref)}
          className="wikilink"
          data-testid={`inline-link-${ref.type}-${ref.slug}`}
        >
          {matched}
        </Link>
      );
      last = m.index + matched.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out.length ? out : text;
  };
}

export default function ContentBody({ nodes = [], linkRefs = [] }) {
  const linkify = React.useMemo(() => buildLinker(linkRefs), [linkRefs]);

  const rendered = [];
  let i = 0;
  let liKey = 0;
  while (i < nodes.length) {
    const n = nodes[i];
    if (n.type === "h2") {
      rendered.push(
        <h2 id={anchorId(n.text)} key={`h2-${i}`}>
          {n.text}
        </h2>
      );
      i++;
    } else if (n.type === "h3") {
      rendered.push(
        <h3 id={anchorId(n.text)} key={`h3-${i}`}>
          {n.text}
        </h3>
      );
      i++;
    } else if (n.type === "li") {
      const items = [];
      while (i < nodes.length && nodes[i].type === "li") {
        items.push(
          <li key={`li-${i}`}>{linkify(nodes[i].text, `li-${i}`)}</li>
        );
        i++;
      }
      rendered.push(<ul key={`ul-${liKey++}`}>{items}</ul>);
    } else if (n.type === "code") {
      rendered.push(<pre key={`code-${i}`}><code>{n.text}</code></pre>);
      i++;
    } else {
      rendered.push(<p key={`p-${i}`}>{linkify(n.text, `p-${i}`)}</p>);
      i++;
    }
  }

  return <div className="prose-cb">{rendered}</div>;
}

export function buildToc(nodes = []) {
  return nodes
    .filter((n) => n.type === "h2")
    .map((n) => ({ id: anchorId(n.text), text: n.text }));
}
